import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  classroomContextId,
  withClassroomContext,
} from "@/components/workspace/navigation";
import { IoCloudUploadOutline } from "react-icons/io5";
import {
  Users,
  Square,
  Pause,
  Play,
  ChevronLeft,
  PencilLine,
  Eye,
} from "lucide-react";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { useSpaceStore, type SpaceStatus } from "../store/useSpaceStore";
import { useSpace } from "../hooks/useSpace";
import { useViewport } from "@/hooks/useViewport";
import { formatDuration, formatLastSaved } from "../spacesTimeUtils";
import { getAvatarColor } from "../spacesPresenceUtils";
import "../space-live.css";

// Fallback wait before navigating to recap if the ctrl WS space_closed
// broadcast hasn't arrived. Long enough that a healthy ctrl channel almost
// always redirects first, short enough to not strand the teacher.
const SPACE_CLOSE_REDIRECT_FALLBACK_MS = 5_000;

// Allow "loading" alongside the canonical SpaceStatus — pages typically
// initialise their local status state to "loading" before the join request
// resolves, and they pass that value straight to this prop.
type Props = {
  spaceStatus: SpaceStatus | "loading";
  onSave?: () => Promise<void>;
  lastSaved?: Date | null;
  participantPanelOpen?: boolean;
  onToggleParticipantPanel?: () => void;
  onEdit?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onPublish?: () => Promise<void>;
  onSaveDraft?: () => Promise<void>;
  startedAt?: Date | null;
  // When set, the teacher is in "watching student work" mode: the left side of
  // the bar swaps the space name for the student identity and the Back button
  // exits the watching view. The right-side live controls stay unchanged. Pass
  // the student identity (id + display name) and an exit callback
  // (onStopWatching). The id is what we hash for the avatar swatch — names
  // differ between API sources and would produce mismatched colors otherwise.
  watchingStudentId?: number | null;
  watchingStudentName?: string | null;
  watchingTimeSpentMs?: number | null;
  onStopWatching?: () => void;
  // Presentation variant for watching mode (ignored when not watching):
  // - "live": teacher peeking at a student's canvas in an active space — indigo
  //   gradient + "Watching" eyebrow with an eye icon, no time-spent stat.
  // - "recap": teacher reviewing a saved snapshot in a closed space — no
  //   gradient, "Student" eyebrow, time-spent stat shown.
  watchMode?: "live" | "recap";
  // Override the spaceId/spaceName pulled from useSpaceStore. Needed on pages
  // that render SpaceTopBar without having called /join first (e.g. recap
  // snapshot viewer), so the store is unpopulated.
  spaceIdOverride?: string | null;
  spaceNameOverride?: string | null;
};

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  const ss = String(secs).padStart(2, "0");
  return mins > 0 ? `${mins}m ${ss}s` : `${ss}s`;
}

export default function SpaceTopBar({
  spaceStatus,
  onSave,
  lastSaved,
  participantPanelOpen,
  onToggleParticipantPanel,
  onEdit,
  onPause,
  onResume,
  onPublish,
  onSaveDraft,
  startedAt,
  watchingStudentId,
  watchingStudentName,
  watchingTimeSpentMs,
  onStopWatching,
  watchMode = "live",
  spaceIdOverride,
  spaceNameOverride,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const classroomId = classroomContextId(location.pathname, location.search);
  const closeFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  useEffect(
    () => () => {
      if (closeFallbackTimerRef.current !== null)
        clearTimeout(closeFallbackTimerRef.current);
    },
    [],
  );
  const [isClosing, setIsClosing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [elapsed, setElapsed] = useState("");

  useEffect(() => {
    if (!startedAt) return;
    const tick = () =>
      setElapsed(formatElapsed(Date.now() - startedAt.getTime()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  const { isMobilePortrait } = useViewport();

  const spaceIdFromStore = useSpaceStore((s) => s.spaceId);
  const spaceNameFromStore = useSpaceStore((s) => s.spaceName);
  const spaceId = spaceIdOverride ?? spaceIdFromStore;
  const spaceName = spaceNameOverride ?? spaceNameFromStore;

  const { closeSpace } = useSpace();
  const { showNotification } = useNotificationStore();

  const isWatching = !!watchingStudentName;
  const isRecapWatch = isWatching && watchMode === "recap";
  const isLiveWatch = isWatching && watchMode === "live";

  if (!spaceId) return null;

  const isOpen = spaceStatus === "open";
  const isPaused = spaceStatus === "paused";
  const isClosed = spaceStatus === "closed";
  const isDraft = spaceStatus === "draft";
  const isLive = isOpen || isPaused;
  const watchingAvatar = watchingStudentName
    ? getAvatarColor(watchingStudentId ?? watchingStudentName)
    : null;

  const handleEnd = async () => {
    if (isClosing) return;
    setIsClosing(true);
    try {
      await closeSpace(spaceId);
      showNotification("Space closed", NotificationType.SUCCESS);
      // Primary redirect: TeacherSpacePage.onSpaceClosed fires when the ctrl WS
      // receives the space_closed broadcast. If the ctrl channel is down or slow,
      // fall back after 5 s so the teacher is never stuck on the page.
      closeFallbackTimerRef.current = setTimeout(
        () => navigate(`/spaces/${spaceId}/recap`),
        SPACE_CLOSE_REDIRECT_FALLBACK_MS,
      );
    } catch {
      showNotification("Failed to close space", NotificationType.WARNING);
      setIsClosing(false);
    }
  };

  const handleSave = async () => {
    if (!onSave || isSaving) return;
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!onSaveDraft || isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      await onSaveDraft();
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!onPublish || isPublishing) return;
    setIsPublishing(true);
    try {
      await onPublish();
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div
      className="et-space-topbar flex items-center justify-between px-4 py-2 shadow-sm z-10 shrink-0 font-mulish transition-colors duration-300"
      style={{
        fontFamily: "Mulish, sans-serif",
        // While watching a live student, blend a subtle indigo gradient over the
        // green backdrop to reinforce that the teacher has left the shared canvas.
        // The recap snapshot view is static, so it keeps the plain green backdrop.
        ...(isLiveWatch && {
          backgroundImage:
            "linear-gradient(90deg, rgba(99, 102, 241, 0.42) 0%, rgba(99, 102, 241, 0.12) 50%, rgba(99, 102, 241, 0) 100%)",
        }),
      }}
    >
      {/* Left: Back / Leave Space + space name. While watching a student, this
          button exits the watching view (back to shared) instead of leaving the
          space — the watching chip on the right only deduplicates that affordance. */}
      <div className="flex gap-3 items-center">
        <button
          onClick={() => {
            if (isWatching && onStopWatching) onStopWatching();
            else if (isClosed) navigate(-1);
            else navigate(withClassroomContext("/spaces", classroomId));
          }}
          className="et-space-chip flex items-center gap-1.5 h-9 px-4 rounded-[6px] text-sm font-medium font-mulish transition-colors"
        >
          <ChevronLeft className="w-4 h-4 shrink-0 block" />
          {!isMobilePortrait && (
            // Crossfade the label so the button width stays put when the text
            // swaps between "Leave Space" and "Back" — the hidden longest label
            // reserves the width, the two visible ones fade in/out on top.
            <span className="relative inline-flex items-center">
              <span aria-hidden className="invisible">
                Leave Space
              </span>
              <span
                className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
                style={{ opacity: isWatching || isClosed ? 1 : 0 }}
              >
                Back
              </span>
              <span
                className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
                style={{ opacity: isWatching || isClosed ? 0 : 1 }}
              >
                Leave Space
              </span>
            </span>
          )}
        </button>

        <div className="w-px h-8 bg-white/30" />

        {/* Identity slot: in watching mode this becomes the student identity
            (avatar + name with a "Watching" eyebrow), pushing the space name out
            of focus. Otherwise we render the space name as before. */}
        {isWatching && watchingAvatar ? (
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
              style={{
                backgroundColor: watchingAvatar.bg,
                color: watchingAvatar.text,
              }}
            >
              {watchingStudentName!.charAt(0).toUpperCase()}
            </div>
            <div className="flex flex-col justify-center">
              {isRecapWatch ? (
                <span className="text-[10px] font-semibold text-white/60 uppercase tracking-widest font-mulish">
                  Student
                </span>
              ) : (
                <span className="flex items-center gap-1 text-[10px] font-semibold text-white/60 uppercase tracking-widest font-mulish">
                  <Eye className="w-3 h-3 shrink-0" />
                  Watching
                </span>
              )}
              <p
                className={`font-bold text-white font-mulish leading-tight ${isMobilePortrait ? "text-sm" : "text-base"}`}
              >
                {watchingStudentName}
              </p>
            </div>
            {isRecapWatch && watchingTimeSpentMs != null && (
              <>
                <div className="w-px h-8 bg-white/30" />
                <div className="flex flex-col justify-center">
                  <span className="text-[10px] font-semibold text-white/60 uppercase tracking-widest font-mulish">
                    Time Spent
                  </span>
                  <p
                    className={`font-bold text-white font-mulish leading-tight ${isMobilePortrait ? "text-sm" : "text-base"}`}
                  >
                    {formatDuration(watchingTimeSpentMs)}
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col justify-center">
            <span className="text-[10px] font-semibold text-white/60 uppercase tracking-widest font-mulish">
              Space
            </span>
            <p
              className={`font-bold text-white font-mulish leading-tight ${isMobilePortrait ? "text-sm" : "text-base"}`}
            >
              {spaceName}
            </p>
          </div>
        )}
      </div>

      {/* Right: context-dependent controls */}
      <div className="flex items-center gap-3">
        {/* Draft controls */}
        {isDraft && (
          <>
            {onSaveDraft && (
              <button
                onClick={handleSaveDraft}
                disabled={isSavingDraft}
                title="Save draft"
                className="flex flex-col items-center gap-0.5 text-white/60 hover:text-white transition-colors disabled:opacity-40"
              >
                <IoCloudUploadOutline
                  className={`w-5 h-5 ${isSavingDraft ? "animate-pulse" : ""}`}
                />
                <span className="text-[10px] leading-none w-16 text-center">
                  {isSavingDraft ? "Saving…" : formatLastSaved(lastSaved)}
                </span>
              </button>
            )}
            {onPublish && (
              <button
                onClick={handlePublish}
                disabled={isPublishing}
                className="et-space-primary flex items-center h-9 px-4 rounded-[6px] text-sm font-semibold font-mulish transition-colors disabled:opacity-60"
              >
                {isPublishing ? "Publishing…" : "Publish"}
              </button>
            )}
          </>
        )}

        {/* Live / closed controls */}
        {!isDraft && (
          <>
            {/* Save icon — only when live */}
            {onSave && isLive && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                title="Save now"
                className="flex flex-col items-center gap-0.5 text-white/60 hover:text-white transition-colors disabled:opacity-40"
              >
                <IoCloudUploadOutline
                  className={`w-5 h-5 ${isSaving ? "animate-pulse" : ""}`}
                />
                <span className="text-[10px] leading-none w-16 text-center">
                  {isSaving ? "Saving…" : formatLastSaved(lastSaved)}
                </span>
              </button>
            )}

            {/* Participants toggle — only when live */}
            {onToggleParticipantPanel && isLive && (
              <button
                onClick={onToggleParticipantPanel}
                title={
                  participantPanelOpen
                    ? "Hide participants"
                    : "Show participants"
                }
                className={`flex items-center justify-center w-9 h-9 rounded-[6px] transition-colors ${
                  participantPanelOpen ? "et-space-primary" : "et-space-chip"
                }`}
              >
                <Users className="w-4 h-4" />
              </button>
            )}

            {/* Live-only: status pill + pause/resume + end */}
            {isLive && (
              <>
                {isPaused ? (
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-semibold border border-transparent">
                    <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                    {!isMobilePortrait && "PAUSED"}
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-sm font-semibold">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                    {!isMobilePortrait && "LIVE"}
                  </span>
                )}
                {!isMobilePortrait && elapsed && (
                  <span className="text-white/70 text-sm font-normal tabular-nums">
                    {elapsed}
                  </span>
                )}

                {onEdit && (
                  <button
                    onClick={onEdit}
                    title="Edit space"
                    className="et-space-chip flex items-center justify-center w-10 h-10 rounded-[6px] transition-colors"
                  >
                    <PencilLine className="w-4 h-4" />
                  </button>
                )}

                {isPaused
                  ? onResume && (
                      <button
                        onClick={onResume}
                        title="Resume space"
                        className="flex items-center justify-center w-10 h-10 rounded-[6px] bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                      >
                        <Play className="w-4 h-4 fill-amber-700" />
                      </button>
                    )
                  : onPause && (
                      <button
                        onClick={onPause}
                        title="Pause space"
                        className="et-space-chip flex items-center justify-center w-10 h-10 rounded-[6px] transition-colors"
                      >
                        <Pause className="w-4 h-4 fill-white" />
                      </button>
                    )}

                <button
                  onClick={handleEnd}
                  disabled={isClosing}
                  className="et-space-danger flex items-center gap-1.5 h-9 px-4 rounded-[6px] text-sm font-semibold font-mulish transition-colors disabled:opacity-60"
                >
                  <Square className="w-3.5 h-3.5 fill-white" />
                  {!isMobilePortrait && (isClosing ? "Ending…" : "End")}
                </button>
              </>
            )}

            {/* Closed: nothing extra — just Leave Space on the left */}
            {isClosed && null}
          </>
        )}
      </div>
    </div>
  );
}
